import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import { landingColors } from '../../../theme/landingTheme';

interface PricingSectionProps {
  onRequestDemo: () => void;
}

const plans = [
  {
    name: 'Free',
    price: '0',
    period: 'forever',
    description: 'Perfect for getting started',
    features: [
      '1 Virtual Location',
      'Up to 50 Products',
      'Basic Inventory Tracking',
      'Sales Management',
      'Client Management (100 max)',
      'Email Support',
    ],
    cta: 'Get Started',
    highlighted: false,
    color: landingColors.textSecondary,
  },
  {
    name: 'Pro',
    price: '49',
    period: 'per month',
    description: 'For growing businesses',
    features: [
      'Everything in Free, plus:',
      'Up to 3 Locations',
      'Unlimited Products',
      'Point of Sale System',
      'Reservations & Appointments',
      'Stock Transfers',
      'B2B Features',
      'Priority Support',
    ],
    cta: 'Request Demo',
    highlighted: true,
    color: landingColors.primary,
    badge: 'Most Popular',
  },
  {
    name: 'Enterprise',
    price: '149',
    period: 'per month',
    description: 'For large operations',
    features: [
      'Everything in Pro, plus:',
      'Unlimited Locations',
      'Advanced Analytics',
      'Multi-warehouse Support',
      'Custom B2B Portal',
      'Delivery & Logistics',
      'API Access',
      'Dedicated Account Manager',
    ],
    cta: 'Contact Sales',
    highlighted: false,
    color: landingColors.secondary,
  },
];

export const PricingSection: React.FC<PricingSectionProps> = ({ onRequestDemo }) => {
  return (
    <Box
      id="pricing"
      component="section"
      sx={{
        py: { xs: 10, md: 16 },
        position: 'relative',
        background: `
          radial-gradient(ellipse at 50% 0%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
          ${landingColors.backgroundAlt}
        `,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 640,
            mx: 'auto',
            mb: { xs: 6, md: 10 },
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'inline-block',
              mb: 2,
              px: 2,
              py: 0.5,
              backgroundColor: landingColors.primaryMuted,
              borderRadius: '6px',
            }}
          >
            Pricing
          </Typography>
          <Typography variant="h2" sx={{ mb: 3 }}>
            Simple, Transparent Pricing
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.125rem' },
              color: landingColors.textSecondary,
            }}
          >
            Choose the plan that fits your business. Upgrade or downgrade anytime.
            All plans include a 14-day free trial.
          </Typography>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={3} alignItems="stretch">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: plan.highlighted
                    ? `2px solid ${landingColors.primary}`
                    : `1px solid ${landingColors.border}`,
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: plan.highlighted ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: plan.highlighted
                    ? '0 24px 48px rgba(99, 102, 241, 0.15)'
                    : 'none',
                  '&:hover': {
                    transform: plan.highlighted ? 'scale(1.04)' : 'scale(1.02)',
                    boxShadow: plan.highlighted
                      ? '0 32px 64px rgba(99, 102, 241, 0.2)'
                      : '0 16px 32px rgba(15, 23, 42, 0.08)',
                  },
                }}
              >
                {/* Popular Badge */}
                {plan.badge && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 16 }} />}
                    label={plan.badge}
                    sx={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 28,
                      px: 1,
                      '& .MuiChip-icon': {
                        color: '#fff',
                      },
                    }}
                  />
                )}

                <CardContent
                  sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  {/* Plan Name */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: plan.color,
                      mb: 1,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Price */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Cabinet Grotesk", sans-serif',
                          fontSize: '3rem',
                          fontWeight: 700,
                          color: landingColors.textPrimary,
                          lineHeight: 1,
                        }}
                      >
                        ${plan.price}
                      </Typography>
                      <Typography
                        sx={{
                          color: landingColors.textMuted,
                          fontSize: '0.875rem',
                        }}
                      >
                        /{plan.period}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: landingColors.textSecondary,
                      mb: 3,
                    }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Features */}
                  <Box sx={{ flex: 1, mb: 3 }}>
                    {plan.features.map((feature, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <CheckCircleIcon
                          sx={{
                            fontSize: 20,
                            color: i === 0 && index > 0 ? landingColors.textMuted : plan.color,
                            mt: 0.25,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              i === 0 && index > 0
                                ? landingColors.textMuted
                                : landingColors.textPrimary,
                            fontWeight: i === 0 && index > 0 ? 500 : 400,
                            fontStyle: i === 0 && index > 0 ? 'italic' : 'normal',
                          }}
                        >
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    fullWidth
                    variant={plan.highlighted ? 'contained' : 'outlined'}
                    size="large"
                    onClick={onRequestDemo}
                    sx={{
                      py: 1.5,
                      borderRadius: '12px',
                      ...(plan.highlighted
                        ? {
                            background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.35)',
                            '&:hover': {
                              boxShadow: '0 12px 32px rgba(99, 102, 241, 0.45)',
                            },
                          }
                        : {
                            borderColor: landingColors.border,
                            borderWidth: 2,
                            color: landingColors.textPrimary,
                            '&:hover': {
                              borderWidth: 2,
                              borderColor: plan.color,
                              backgroundColor: `${plan.color}08`,
                            },
                          }),
                    }}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: landingColors.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: landingColors.success }} />
              14-day free trial
            </Box>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: landingColors.success }} />
              No credit card required
            </Box>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 16, color: landingColors.success }} />
              Cancel anytime
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PricingSection;
